import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { History, RefreshCw, Undo2 } from "lucide-react";

export const OverwriteHistory = ({ open, onClose, onRestored }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/order-register/overwrite-history`,
        { headers: getAuthHeader(), params: { days: 365 } });
      setEvents(r.data.events || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (jobId, parcelKey) => {
    const key = `${jobId}|${parcelKey}`;
    setRestoring(key);
    try {
      const r = await axios.post(`${API}/order-register/overwrite-history/undo`,
        { job_id: jobId, parcel_key: parcelKey }, { headers: getAuthHeader() });
      toast.success(`Restored ${r.data.restored_rows} old row(s), removed ${r.data.removed_new_rows} new row(s)`);
      load(); onRestored?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setRestoring(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="overwrite-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4" /> Overwrite History
            <Button size="sm" variant="ghost" className="ml-auto" onClick={load} disabled={loading} data-testid="overwrite-history-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Old rows replaced by "Overwrite" during uploads. Restore puts the old rows back and removes the new rows that replaced them.</p>
        {events.length === 0 && !loading && (
          <div className="text-sm text-slate-400 py-8 text-center" data-testid="overwrite-history-empty">No overwritten orders yet.</div>
        )}
        <div className="space-y-4">
          {events.map(ev => (
            <div key={ev.job_id} className="border border-slate-200 rounded-lg" data-testid={`overwrite-event-${ev.job_id}`}>
              <div className="px-3 py-2 bg-slate-50 border-b text-xs text-slate-600 flex flex-wrap gap-2">
                <span>{new Date(ev.at).toLocaleString()}</span>·<span>by <b>{ev.by}</b></span>·
                <span>{ev.parcels.length} parcel(s)</span>
              </div>
              {ev.parcels.map(p => {
                const head = p.rows[0] || {};
                const key = `${ev.job_id}|${p.parcel_key}`;
                return (
                  <div key={p.parcel_key} className="px-3 py-2 border-b last:border-0" data-testid={`overwrite-parcel-${p.parcel_key}`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <b>{head.customer_name || "—"}</b>
                      <span className="text-xs text-slate-500">{head.code} · {head.date} · {head.mode}</span>
                      <span className="text-xs font-mono px-1.5 rounded bg-slate-100">{head.tracking || "no tracking"}</span>
                      {p.restored
                        ? <span className="ml-auto text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Restored</span>
                        : <Button size="sm" variant="outline" className="ml-auto" disabled={restoring === key}
                                  onClick={() => restore(ev.job_id, p.parcel_key)}
                                  data-testid={`overwrite-restore-${p.parcel_key}`}>
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Restore
                          </Button>}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">
                      {p.rows.map((r, i) => (
                        <div key={i}>{r.medicine} × {r.quantity}{r.invoice_no ? ` · Inv ${r.invoice_no}` : ""}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
